import { useMemo } from 'react'
import { useReadContract, useReadContracts } from 'wagmi'
import { ADDRESSES, ZERO_ADDR } from '@/lib/contracts'
import multiplyAbi from '@/lib/abis/MultiplyRouter.json'
import { POLL_SLOW } from '@/lib/utils'
import { useProtocolState } from '@/hooks/useProtocolState'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'
import { useLstApr, intrinsicAprBpsFor } from '@/hooks/useLstApr'
import { LOOPS, maxLeverage, netApyBps, loopLegRates } from '@/lib/loops'
import { toNumber } from '@/lib/utils'

const YEAR_SECONDS = 365 * 24 * 60 * 60

/// The deposit the headline "net APY at max leverage" is quoted on: $10,000 of equity opened at
/// the loop's ceiling. Same figure and same reasoning as EarnSection's cold-start reference -
/// large enough to be reachable rather than a dust artifact, small enough to be a plausible first
/// open - and it matters for the same reason: the plain APR math reports 0% on an empty reserve,
/// which made the loops that most need a first opener look like they pay nothing.
const REF_OPEN_USD = 10_000


/// Every loop, composed from live reserve data plus the router's own swap-venue configuration.
///
/// The venue read is the difference between an honest "not live" and a button that reverts
/// SwapPoolNotConfigured on submit: `deployLocal` leaves the pair unset on any chain without a real
/// wstETH/ETH pool, which is every fork. Read for ALL loops rather than just the first - the list
/// had only ever checked `LOOPS[0]`, which was correct while there was one loop and silently wrong
/// the moment there were two.
export function useLoops() {
  const { d, isLoading, deployed, refetch } = useProtocolState()
  const { rate: horizonUsd, found: hasHorizonPrice } = useHorizonPriceUsd()
  const { aprBps: lstAprBps } = useLstApr()

  const router = ADDRESSES.MULTIPLY_ROUTER
  const routerLive = !!router && router !== ZERO_ADDR

  // Resolved before the venue reads, because each read needs its pair's two asset addresses.
  const pairs = useMemo(() => LOOPS.map((loop) => {
    const collateral = d.reserves.find((r) => r.key === loop.collateralKey)
    const debt = d.reserves.find((r) => r.key === loop.debtKey)
    return collateral && debt ? { loop, collateral, debt } : null
  }), [d.reserves])

  const resolved = pairs.filter(Boolean)

  const { data: venueReads } = useReadContracts({
    query: { enabled: routerLive && resolved.length > 0, refetchInterval: POLL_SLOW },
    contracts: resolved.map(({ collateral, debt }) => ({
      address: router, abi: multiplyAbi, functionName: 'swapPools', args: [collateral.asset, debt.asset],
    })),
  })

  // Router-wide, not per-pair: opening is paused for every loop at once or none. Read separately
  // so the form can say WHY it is closed instead of letting the transaction revert
  // `OpeningIsPaused` after the user has already signed. Closing is never gated by it.
  const { data: openingPaused } = useReadContract({
    address: router, abi: multiplyAbi, functionName: 'openingPaused',
    query: { enabled: routerLive, refetchInterval: POLL_SLOW },
  })

  // The venue DORMANCY gate, and it is read for a specific reason: a configured venue is not the
  // same as a usable one. `SwapVenue.verifyAndWarm` refuses to swap through a pool until
  // `setAt + minPoolAgeSeconds` has passed, because a freshly-pointed pool has no observation
  // history and a TWAP floor read against it is arithmetic without meaning.
  //
  // Without this read the UI treats "configured" as "live" and offers an Open button that reverts -
  // exactly the failure `configured` itself was added to prevent, one step further along. Found by
  // checking the dev chain rather than by review: the venue was correctly pointed at the real
  // wstETH/WETH 0.01% pool and still had six days left on the timer.
  const { data: minPoolAgeSeconds } = useReadContract({
    address: router, abi: multiplyAbi, functionName: 'minPoolAgeSeconds',
    query: { enabled: routerLive, refetchInterval: POLL_SLOW },
  })

  const loops = useMemo(() => resolved.map(({ loop, collateral, debt }, i) => {
    const cfg = venueReads?.[i]?.result
    const venuePool = cfg?.[0]
    const configured = routerLive && !!venuePool && venuePool !== ZERO_ADDR

    // swapPools returns (pool, feeTier, setAt). Dormancy is judged in SECONDS against the chain's
    // own clock, converted to ms only for display - mixing the two units is how an off-by-1000 bug
    // ends up telling someone to come back in a thousand years.
    const setAtSec = cfg?.[2] != null ? Number(cfg[2]) : null
    const minAgeSec = minPoolAgeSeconds != null ? Number(minPoolAgeSeconds) : null
    const venueReadyAtSec = setAtSec != null && minAgeSec != null ? setAtSec + minAgeSec : null
    const venueDormant = configured && venueReadyAtSec != null && Date.now() / 1000 < venueReadyAtSec

    const lev = maxLeverage(collateral.collateralFactorBps)
    // The all-in legs: interest, HORIZON on both sides, and the collateral's intrinsic yield
    // (wstETH staking, which accrues by exchange rate and is invisible to every pool measure).
    // Reward streams are quoted deposit-aware on a $10k reference open at max leverage, so an
    // empty reserve shows the real rate its first opener would capture instead of 0.00% - see
    // REF_OPEN_USD above and dilutedRewardAprBps in lib/loops.
    const { earnBps, costBps } = loopLegRates({
      supplyRateBps: collateral.supplyRateBps,
      borrowRateBps: debt.borrowRateBps,
      intrinsicAprBps: intrinsicAprBpsFor(collateral.key, lstAprBps),
      // BUDGET streams bounded by the governor caps, never today's effective rates - see
      // loopLegRates' own comment for why (the entry-198 bug class, leveraged).
      supplyEmissionsUsdPerYear: hasHorizonPrice ? toNumber(collateral.supplyHorizonBudgetPerSecond) * YEAR_SECONDS * horizonUsd : 0,
      borrowEmissionsUsdPerYear: hasHorizonPrice ? toNumber(debt.borrowHorizonBudgetPerSecond) * YEAR_SECONDS * horizonUsd : 0,
      supplyCapBps: collateral.supplyAprCapBps,
      borrowCapBps: debt.borrowAprCapBps,
      suppliedUsd: collateral.totalSupplied * collateral.priceUsd,
      borrowedUsd: debt.totalBorrows * debt.priceUsd,
      supplyAddUsd: REF_OPEN_USD * lev,
      borrowAddUsd: REF_OPEN_USD * (lev - 1),
    })
    return {
      venueReadyAtSec,
      venueDormant,
      ...loop,
      collateral,
      debt,
      leverage: lev,
      netApyBps: netApyBps(collateral.supplyRateBps, debt.borrowRateBps, lev),
      // The all-in figure the founder asked for (item 195), now deposit-aware and yield-inclusive.
      // Interest-only stays alongside as the carry.
      allInNetApyBps: netApyBps(earnBps, costBps, lev),
      // The effective legs, exported so the detail page's chart tells the same story as this
      // headline instead of recomputing from reserve-level totals.
      allInEarnBps: earnBps,
      allInCostBps: costBps,
      // What a looper can actually borrow, which is the real ceiling on a loop rather than the
      // headline leverage: with nothing to borrow, the loop stops on its first pass.
      liquidityUsd: Math.max(debt.totalSupplied - debt.totalBorrows, 0) * debt.priceUsd,
      suppliedUsd: collateral.totalSupplied * collateral.priceUsd,
      borrowedUsd: debt.totalBorrows * debt.priceUsd,
      configured,
      openingPaused: !!openingPaused,
      venuePool: configured ? venuePool : null,
      // `SwapPoolConfig` is (pool, feeTier, setAt) and nothing else. An earlier version of this file
      // read the third field as a "TWAP window", which it has never been - so Advanced Info was
      // rendering a unix timestamp as a duration. The TWAP window lives in `SwapVenue`, not in this
      // struct. Surfaced as what it actually is: when genesis pointed this pair at its venue.
      venueFee: cfg?.[1] != null ? Number(cfg[1]) : null,
      venueSetAtSec: setAtSec,
    }
  }), [resolved, venueReads, routerLive, openingPaused, minPoolAgeSeconds, horizonUsd, hasHorizonPrice, lstAprBps])

  return { loops, isLoading, deployed, refetch, d, routerLive }
}

/// Single-loop counterpart, with the same `{ notFound, notDeployed }` contract useVaultDetail and
/// useReserveDetail use - so /multiply/:id can render the identical layered guards.
export function useLoopDetail(id) {
  const { loops, isLoading, deployed, refetch, d } = useLoops()

  // Checked against the STATIC list rather than the composed one: a loop whose reserves have yet
  // to load is loading, and a loop that was never defined is genuinely not found. Collapsing those
  // two into one check reported "not found" for a real loop during every page load.
  const known = LOOPS.some((l) => l.id === id)
  const loop = loops.find((l) => l.id === id)

  if (!deployed) return { loop: null, isLoading: false, notFound: false, notDeployed: true, refetch, d }
  if (!known) return { loop: null, isLoading: false, notFound: true, notDeployed: false, refetch, d }
  if (isLoading || !loop) return { loop: null, isLoading: true, notFound: false, notDeployed: false, refetch, d }

  return { loop, isLoading: false, notFound: false, notDeployed: false, refetch, d }
}
